import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@stores/store';
import { Box, Menu, MenuItem, IconButton, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useAddUserPreferenceMutation } from '@api/userDetails';
import { LANGUAGES } from '@constants/constants';
import i18n from '@utils/i18next';
import { useTranslation } from 'react-i18next';

interface LanguageSelectorProps {
  anchorEl: null | HTMLElement;
  setAnchorEl: React.Dispatch<React.SetStateAction<null | HTMLElement>>;
}

const languageCodeMap: Record<string, string> = {
  EN: 'en',
  ZH: 'zh-CN',
};
// LANGUAGES labels English as 'EN' for the signup page; other languages use their LANGUAGES label
const languageDisplayLabels: Record<string, string> = {
  en: 'English',
};

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ anchorEl, setAnchorEl }) => {
  const open = Boolean(anchorEl);
  const { t: translation } = useTranslation();

  const userData = useSelector((state: RootState) => state.UserData);
  const [setPreference] = useAddUserPreferenceMutation();

  useEffect(() => {
    const updateUserPreferences = async () => {
      try {
        const userLocale = languageCodeMap[userData?.user?.preference?.language || 'EN'];
        const urlLocale = new URLSearchParams(window.location.search).get('locale');

        if (urlLocale && LANGUAGES.map((l) => l.locale).includes(urlLocale)) {
          const languageCode =
            LANGUAGES.find((language) => language.locale === urlLocale)?.code || 'EN';
          await setPreference({ language: languageCode });
          i18n.changeLanguage(urlLocale);
          localStorage.setItem('app_locale', urlLocale);
        } else if (userLocale) {
          i18n.changeLanguage(userLocale);
          localStorage.setItem('app_locale', userLocale);
        } else {
          i18n.changeLanguage('en');
          localStorage.setItem('app_locale', 'en');
        }
      } catch (e) {
        console.error(e);
      }
    };

    if (userData?.user?.id) {
      updateUserPreferences();
    }
  }, [userData?.user?.id]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = async (selectedLocale: string) => {
    if (selectedLocale !== i18n.language) {
      i18n.changeLanguage(selectedLocale);
      localStorage.setItem('app_locale', selectedLocale);
      const languageCode =
        LANGUAGES.find((language) => language.locale === selectedLocale)?.code || 'EN';
      try {
        await setPreference({ language: languageCode });
      } catch (e) {
        console.error(e);
      }
    }

    // Remove locale search param from URL if present
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('locale')) {
      urlParams.delete('locale');
      const newUrl = `${window.location.pathname}${urlParams.toString() ? `?${urlParams.toString()}` : ''}${window.location.hash}`;
      window.history.replaceState({}, '', newUrl);
    }

    handleClose();
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        disableRipple
        aria-label="Select language"
        sx={{
          color: '#64748B',
        }}
      >
        <NavigateNextIcon sx={{ fontSize: '20px' }} />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              width: '224px',
              borderRadius: '8px',
              overflow: 'hidden',
            },
          },
        }}
        MenuListProps={{
          sx: {
            padding: '4px',
          },
        }}
      >
        <Typography
          sx={{
            padding: '6px 8px',
            fontSize: '0.875rem',
            fontWeight: 600,
            lineHeight: '1.25rem',
            color: '#032B29',
            borderBottom: '1px solid #EBEEF1',
          }}
        >
          {translation('signup.language')}
        </Typography>
        {LANGUAGES.map((lang) => (
          <MenuItem
            key={lang.locale}
            onClick={() => handleSelect(lang.locale)}
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              padding: '6px 8px',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 400,
                lineHeight: '1.25rem',
                color: '#032B29',
              }}
            >
              {languageDisplayLabels[lang.locale] ?? lang.label}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {i18n.language === lang.locale && (
                <CheckIcon sx={{ fontSize: '1rem', color: '#162C36' }} />
              )}
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LanguageSelector;
